import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationModalComponent } from '../../../shared/modals/confirmation-modal/confirmation-modal.component';
import { RiskFactor } from '../../../../models/risk-factor';
import { RiskFactorType } from '../../../../models/risk-factor-type';
import { AgilityRiskService } from '../../../../providers/agility-risk.service';
import { AgilityRiskFormulaHelperService } from '../../../../providers/agility-risk-formula-helper.service';
import { AgilityRiskSpinnerService } from '../../../../providers/agility-risk-spinner.service';
import { AgilityRiskMessageService } from '../../../../providers/agility-risk-message.service';
import { FormControl, Validators } from '@angular/forms';

@Component({
  selector: 'lib-risk-factor-modal',
  templateUrl: './risk-factor-modal.component.html',
  styleUrls: ['./risk-factor-modal.component.scss']
})
export class RiskFactorModalComponent implements OnInit {

  public factor: RiskFactor;
  public formulaId: string;
  public submitted = false;
  public ranksAreDeletable = true;

  public columns: Array<{ header: string; class: string }> = [

    {
      header: 'common.name',
      class: 'agility-col-100'
    },
    {
      header: 'common.value',
      class: 'agility-col-30'
    },
    {
      header: 'common.description',
      class: 'agility-col-250'
    },
    {
      header: 'common.delete',
      class: 'agility-col-30'
    }
  ];

  factorNameCtrl = new FormControl('', Validators.required);

  private riskFactors: RiskFactor[];

  constructor(private translateService: TranslateService,
              private dialogService: DialogService,
              private dlgRef: DynamicDialogRef,
              private dialogConfig: DynamicDialogConfig,
              private agilityRiskService: AgilityRiskService,
              private messageService: AgilityRiskMessageService,
              private formulaHelper: AgilityRiskFormulaHelperService,
              private spinner: AgilityRiskSpinnerService) {
    this.columns = this.columns.map(c => {
      c.header = (c.header) ? this.translateService.instant(c.header) : '';
      return c;
    });
  }

  ngOnInit(): void {

    this.riskFactors = (this.dialogConfig.data?.riskFactors) ? this.dialogConfig.data?.riskFactors : [];

    if (this.dialogConfig.data?.factor) {
      this.factor = this.dialogConfig.data.factor;
      this.formulaId = this.dialogConfig.data.formulaId;
      this.factor.ranks = this.factor.ranks.sort((a, b) => (a.numericValue > b.numericValue) ? 1 : (a.numericValue < b.numericValue) ? -1 : 0);
    } else {
      this.formulaId = this.dialogConfig.data.formulaId;
      this.factor = {
        id: null,
        type: RiskFactorType.userDefined,
        name: '',
        description: '',
        ranks: [{
          id: null,
          name: 'Low',
          numericValue: 1,
          description: 'Low'
        }, {
          id: null,
          name: 'Medium',
          numericValue: 2,
          description: 'Medium'
        }, {
          id: null,
          name: 'High',
          numericValue: 3,
          description: 'High'
        }]
      };
    }

    this.ranksAreDeletable = this.factor.ranks.length > 2;
  }

  onRankReorder(dragIndex, dropIndex) {
    // just re-order the rank factors
    this.factor.ranks = this.factor.ranks.map((r, index) => {
      r.numericValue = index + 1;
      return r;
    });
  }

  onAddRank() {
    this.factor.ranks.push({
      id: null,
      name: '',
      numericValue: this.factor.ranks.length + 1,
      description: ''
    });
    this.factor.ranks = this.factor.ranks.map(r => r);
    this.ranksAreDeletable = this.factor.ranks.length > 2;
  }

  onDeleteRank(rankId: string, index: number) {
    const confirmationDynamicDialogRef = this.dialogService.open(ConfirmationModalComponent, {
      data: {
        body: this.translateService.instant('formulaManagement.editFactorModal.deleteFactorRankBody')
      },
      header: this.translateService.instant('formulaManagement.editFactorModal.deleteFactorRank'),
      width: '35%'
    });

    confirmationDynamicDialogRef.onClose.subscribe(res => {
      if (res) {
        const indx = rankId ? this.factor.ranks.findIndex(r => r.id === rankId) : index;
        this.factor.ranks.splice(indx, 1);
        this.factor.ranks = this.factor.ranks.map(r => r);
        this.ranksAreDeletable = this.factor.ranks.length > 2;
      }
    });
  }

  onCancel() {
    this.dlgRef.close();
  }

  onSave() {
    if (!this.isValid) {
      this.factorNameCtrl.markAsDirty();
      this.factorNameCtrl.markAllAsTouched();
      return;
    }

    if (!this.isUniqueName) {
      this.messageService.error(this.translateService.instant('common.error'), this.translateService.instant('formulaManagement.editFactorModal.factorNameNotUnique'));
      return;
    }

    // reset numeric values just in case they are messed up
    this.factor.ranks.forEach((r, index) => r.numericValue = index + 1);

    this.spinner.start();
    this.agilityRiskService.saveRiskFactor(this.formulaId, this.factor)
      .subscribe({
        next: res => {
          this.spinner.stop();
          this.messageService.success(this.translateService.instant('common.saved'));
          this.formulaHelper.triggerRefreshFactors(this.formulaId);
          this.dlgRef.close(true);
        },
        error: err => {
          this.messageService.error(this.translateService.instant('common.error'), this.translateService.instant('formulaManagement.editFactorModal.errorSavingRiskFactor'));
          this.spinner.stop();
        }
      });
  }

  get isValid() {
    this.submitted = true;

    const reqFieldsValid = !!(this.factor.name?.length) &&
      !!(this.factor.ranks?.length);
    const rankFieldsValid = this.factor.ranks.some(r => {
      return !(r?.name?.length);
    });

    return reqFieldsValid && !rankFieldsValid;
  }

  get isUniqueName(): boolean {
    if (!this.riskFactors?.length) {
      return true;
    }

    return this.riskFactors.findIndex(rf => rf.id !== this.factor.id && rf.name === this.factor.name) < 0;
  }
}
